import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';

const TABS = ['Company Information', 'Salary Calculation', 'General Settings', 'Reminder Settings'] as const;

const REMINDER_TYPES = [
  { key: 'EP_WP', label: 'EP / Work Permit Expiry' },
  { key: 'CONTRACT', label: 'Contract Expiry' },
  { key: 'PROBATION', label: 'Probation End' },
];

export default function PayrollSystemSettings() {
  const [tab, setTab] = useState<(typeof TABS)[number]>('Company Information');
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    api.get('/payroll/system-settings').then((res) => {
      const s = res.data.settings || {};
      setForm({
        payslipBottomMessage: s.payslip_bottom_message || '',
        gstNo: s.gst_no || '',
        currencyName: s.currency_name || 'SGD',
        uen: s.uen || '',
        cpfRefNumbers: (s.cpf_ref_numbers || []).join(', '),
        otHourlyRateFormula: s.ot_hourly_rate_formula || 'BASIC_OVER_MONTHLY_HOURS',
        shiftAllowanceLinkAmount: s.shift_allowance_link_amount || 0,
        defaultWorkingHoursPerYear: s.default_working_hours_per_year || 2288,
        defaultWorkingDaysPerWeek: s.default_working_days_per_week || 5.5,
        defaultWorkingHoursPerDay: s.default_working_hours_per_day || 8,
        midMonthCpfEnabled: !!s.mid_month_cpf_enabled,
        cpfOnOtRestPhAs: s.cpf_on_ot_rest_ph_as || 'AW',
        deductNsFromNett: s.deduct_ns_from_nett !== false,
        autoRecalculateBeforeUpdate: s.auto_recalculate_before_update !== false,
        calculateDeductionsPerMomRule: s.calculate_deductions_per_mom_rule !== false,
        prorateAllowanceNewJoinResigned: s.prorate_allowance_new_join_resigned !== false,
        autoLeaveEncashmentForResigned: !!s.auto_leave_encashment_for_resigned,
        designationGroupCustomTitle: s.designation_group_custom_title || '',
        reminderSettings: s.reminder_settings || {},
      });
      setLoading(false);
    });
  }, []);

  const set = (key: string, value: any) => setForm((f: any) => ({ ...f, [key]: value }));
  const setReminder = (key: string, field: 'show' | 'daysBefore', value: any) =>
    setForm((f: any) => ({
      ...f,
      reminderSettings: { ...f.reminderSettings, [key]: { ...(f.reminderSettings[key] || {}), [field]: value } },
    }));

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.put('/payroll/system-settings', {
        ...form,
        cpfRefNumbers: form.cpfRefNumbers.split(',').map((s: string) => s.trim()).filter(Boolean),
      });
      setMessage('Settings saved.');
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Payroll System Settings</h1>
      <div className="flex gap-2 mb-6 border-b">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Company Information' && (
        <div className="space-y-4 bg-white p-6 rounded-lg shadow">
          <Input label="Payslip Bottom Message" value={form.payslipBottomMessage} onChange={(e) => set('payslipBottomMessage', e.target.value)} />
          <Input label="GST No." value={form.gstNo} onChange={(e) => set('gstNo', e.target.value)} />
          <Input label="Currency Name" value={form.currencyName} onChange={(e) => set('currencyName', e.target.value)} />
          <Input label="UEN" value={form.uen} onChange={(e) => set('uen', e.target.value)} />
          <Input
            label="CPF Reference Numbers (up to 6, comma-separated)"
            value={form.cpfRefNumbers}
            onChange={(e) => set('cpfRefNumbers', e.target.value)}
          />
        </div>
      )}

      {tab === 'Salary Calculation' && (
        <div className="space-y-4 bg-white p-6 rounded-lg shadow">
          <Select
            label="OT Hourly Rate Formula"
            value={form.otHourlyRateFormula}
            onChange={(e) => set('otHourlyRateFormula', e.target.value)}
            options={[{ value: 'BASIC_OVER_MONTHLY_HOURS', label: 'Basic Salary / Monthly Working Hours (MOM Method 1)' }]}
          />
          <Input
            label="Shift Allowance Link Amount"
            type="number"
            value={form.shiftAllowanceLinkAmount}
            onChange={(e) => set('shiftAllowanceLinkAmount', e.target.value)}
          />
          <Input
            label="Default Working Hours/Year"
            type="number"
            value={form.defaultWorkingHoursPerYear}
            onChange={(e) => set('defaultWorkingHoursPerYear', e.target.value)}
          />
          <Input
            label="Default Working Days/Week"
            type="number"
            step="0.5"
            value={form.defaultWorkingDaysPerWeek}
            onChange={(e) => set('defaultWorkingDaysPerWeek', e.target.value)}
          />
          <Input
            label="Default Working Hours/Day"
            type="number"
            value={form.defaultWorkingHoursPerDay}
            onChange={(e) => set('defaultWorkingHoursPerDay', e.target.value)}
          />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.midMonthCpfEnabled} onChange={(e) => set('midMonthCpfEnabled', e.target.checked)} />
            Enable Mid-Month CPF Contribution (bi-monthly staff)
          </label>
          <Select
            label="CPF on OT/Rest-Day/PH as"
            value={form.cpfOnOtRestPhAs}
            onChange={(e) => set('cpfOnOtRestPhAs', e.target.value)}
            options={[
              { value: 'OW', label: 'Ordinary Wage (OW)' },
              { value: 'AW', label: 'Additional Wage (AW)' },
            ]}
          />
        </div>
      )}

      {tab === 'General Settings' && (
        <div className="space-y-3 bg-white p-6 rounded-lg shadow">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.deductNsFromNett} onChange={(e) => set('deductNsFromNett', e.target.checked)} />
            Deduct NS from nett wage
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.autoRecalculateBeforeUpdate}
              onChange={(e) => set('autoRecalculateBeforeUpdate', e.target.checked)}
            />
            Auto-recalculate before Update
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.calculateDeductionsPerMomRule} disabled />
            Calculate all deductions per MOM rule (fixed on)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.prorateAllowanceNewJoinResigned}
              onChange={(e) => set('prorateAllowanceNewJoinResigned', e.target.checked)}
            />
            Prorate allowance for new-join/resigned
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.autoLeaveEncashmentForResigned}
              onChange={(e) => set('autoLeaveEncashmentForResigned', e.target.checked)}
            />
            Auto leave-encashment pay for resigned
          </label>
          <Input
            label="Designation Group Custom Title"
            value={form.designationGroupCustomTitle}
            onChange={(e) => set('designationGroupCustomTitle', e.target.value)}
          />
        </div>
      )}

      {tab === 'Reminder Settings' && (
        <div className="space-y-4 bg-white p-6 rounded-lg shadow">
          {REMINDER_TYPES.map((r) => (
            <div key={r.key} className="flex items-center gap-4 border-b pb-3">
              <label className="flex items-center gap-2 text-sm w-64">
                <input
                  type="checkbox"
                  checked={!!form.reminderSettings[r.key]?.show}
                  onChange={(e) => setReminder(r.key, 'show', e.target.checked)}
                />
                {r.label}
              </label>
              <Input
                type="number"
                placeholder="Days before expiry"
                value={form.reminderSettings[r.key]?.daysBefore || ''}
                onChange={(e) => setReminder(r.key, 'daysBefore', Number(e.target.value))}
              />
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <Button onClick={save} loading={saving}>
          Save Settings
        </Button>
        {message && <span className="text-sm text-gray-600">{message}</span>}
      </div>
    </div>
  );
}
